import type { Container } from 'pixi.js';
import type { LayerStackAsset } from '../../layerstack/LayerStackLoader';
import { LayerStackSprite } from '../../layerstack/LayerStackSprite';
import { stateNames } from '../../layerstack/ManifestTypes';

/**
 * The loaded layer stacks and the sprites built from them.
 *
 * Sprite management copies PixiRenderer's discipline: a fixed sprite slot per
 * pool slot, `visible` gated on pool.indices.active, integer positions. The one
 * difference is that a LayerStackSprite is bound to one asset, so a slot whose
 * enemy type changed across a respawn swaps to a sprite from that slug's own
 * free list instead of swapping a texture (the loader caches textures, so the
 * swap is a container swap, not a load).
 *
 * Every sprite in the scene is created here, which is what makes advanceVisible()
 * complete: a family cannot accidentally build a sprite that never pulses.
 */

/** One asset plus the sprites built from it that are currently free. */
interface SlugPool {
  readonly asset: LayerStackAsset;
  readonly states: ReadonlySet<string>;
  readonly free: LayerStackSprite[];
  activeCount: number;
}

const NEUTRAL_STATE = 'neutral';
const MOBILE_PHASE_SLICES = 8;
const PULSE_FRAME_MS = 1000 / 8;

export class StackRegistry {
  private readonly pools = new Map<string, SlugPool>();
  /** Every sprite this registry built, for advance() and teardown. */
  private readonly sprites: LayerStackSprite[] = [];
  /**
   * Mobile-only fast path. Long runs can leave many released sprites in
   * `sprites`; keeping the active subset avoids scanning that historical high
   * water mark every frame without changing which animations advance.
   */
  private readonly activeSprites = new Set<LayerStackSprite>();

  constructor(private readonly activeOnlyAdvance = false) {}

  register(slugs: readonly string[], assets: readonly LayerStackAsset[]): void {
    for (let i = 0; i < slugs.length; i += 1) {
      const asset = assets[i]!;
      if (this.pools.has(slugs[i]!)) continue;
      this.pools.set(slugs[i]!, {
        asset,
        states: new Set(stateNames(asset.manifest)),
        free: [],
        activeCount: 0,
      });
    }
  }

  /**
   * Builds a small detached reserve during renderer init. Mobile uses this for
   * bursty projectile/pickup slugs so the first combat volley only reuses an
   * existing LayerStackSprite instead of constructing its child stack in-frame.
   */
  prewarm(slug: string, count: number): void {
    const pool = this.pools.get(slug);
    if (!pool) throw new Error(`R2GameRenderer: no loaded stack for "${slug}"`);
    const missing = Math.max(0, count - pool.free.length);
    for (let i = 0; i < missing; i += 1) {
      const sprite = this.createSprite(pool.asset);
      sprite.setPulsing(true);
      sprite.visible = false;
      this.sprites.push(sprite);
      pool.free.push(sprite);
    }
  }

  /**
   * A free sprite for that slug, or a new one parented to `layer`. Each slug
   * belongs to exactly one family, so a reused sprite is already in the right
   * layer and only a fresh one needs parenting. Every sprite pulses.
   */
  acquire(slug: string, layer: Container | null): LayerStackSprite {
    const pool = this.pools.get(slug);
    if (!pool) throw new Error(`R2GameRenderer: no loaded stack for "${slug}"`);
    const reused = pool.free.pop();
    if (reused) {
      reused.visible = true;
      if (!reused.parent) layer?.addChild(reused);
      if (this.activeOnlyAdvance) this.activeSprites.add(reused);
      pool.activeCount += 1;
      return reused;
    }
    const sprite = this.createSprite(pool.asset);
    sprite.setPulsing(true);
    sprite.visible = true;
    layer?.addChild(sprite);
    this.sprites.push(sprite);
    if (this.activeOnlyAdvance) this.activeSprites.add(sprite);
    pool.activeCount += 1;
    return sprite;
  }

  release(slug: string, sprite: LayerStackSprite): void {
    sprite.visible = false;
    if (this.activeOnlyAdvance) {
      this.activeSprites.delete(sprite);
      sprite.removeFromParent();
    }
    const pool = this.pools.get(slug);
    if (!pool) return;
    pool.activeCount = Math.max(0, pool.activeCount - 1);
    pool.free.push(sprite);
  }

  /** Stage retirement is safe only after mobile families detached their slots. */
  unregister(slug: string): boolean {
    const pool = this.pools.get(slug);
    if (!pool) return true;
    if (pool.activeCount > 0) return false;
    for (const sprite of pool.free) {
      const index = this.sprites.indexOf(sprite);
      if (index >= 0) this.sprites.splice(index, 1);
      sprite.removeFromParent();
      sprite.destroy({ children: true });
    }
    pool.free.length = 0;
    this.pools.delete(slug);
    return true;
  }

  /** Falls back to the master state when a slug does not author the wanted one. */
  applyState(slug: string, sprite: LayerStackSprite, state: string): void {
    const pool = this.pools.get(slug);
    if (pool && pool.states.has(state)) {
      sprite.setState(state);
      return;
    }
    sprite.setState(pool && pool.states.has(NEUTRAL_STATE) ? NEUTRAL_STATE : null);
  }

  advanceVisible(deltaMs: number): void {
    if (this.activeOnlyAdvance) {
      for (const sprite of this.activeSprites) {
        if (sprite.visible) sprite.advance(deltaMs);
      }
      return;
    }
    for (let i = 0; i < this.sprites.length; i += 1) {
      const sprite = this.sprites[i]!;
      if (sprite.visible) sprite.advance(deltaMs);
    }
  }

  clear(): void {
    this.activeSprites.clear();
    // Mobile families detach released stacks from the Pixi tree. Attached
    // stacks are destroyed by R2GameRenderer's layer cascade; only detached
    // instances need explicit teardown here.
    for (const sprite of this.sprites) {
      if (!sprite.parent) sprite.destroy({ children: true });
    }
    this.sprites.length = 0;
    this.pools.clear();
  }

  private createSprite(asset: LayerStackAsset): LayerStackSprite {
    if (!this.activeOnlyAdvance) return new LayerStackSprite(asset);
    const phase = (this.sprites.length % MOBILE_PHASE_SLICES) * (PULSE_FRAME_MS / MOBILE_PHASE_SLICES);
    return new LayerStackSprite(asset, phase);
  }
}
